import React, { useEffect } from 'react';
import { Link, useForm } from '@inertiajs/react';
import MainLayout from '../../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { FiArrowLeft, FiCalendar, FiHome } from 'react-icons/fi';

export default function TourSlotNew({ facilities = [], user }) {
  // Get facility_id from URL if present
  const urlParams = new URLSearchParams(window.location.search);
  const facilityIdFromUrl = urlParams.get('facility_id');
  
  const { data, setData, post, processing, errors } = useForm({
    facility_id: facilityIdFromUrl || '',
    start_at: '',
    end_at: '',
    status: 'open'
  });
  
  // Get the selected facility name for display
  const selectedFacility = facilities.find(f => f.id.toString() === data.facility_id);

  const handleSubmit = (e) => {
    e.preventDefault();
    post('/facility_owner/tour_slots');
  };

  // Handle start time change and automatically set end time to 1 hour later
  const handleStartTimeChange = (e) => {
    const startTime = e.target.value;
    setData('start_at', startTime);
    
    if (startTime) {
      const startDate = new Date(startTime);
      const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour
      
      // Format to YYYY-MM-DDThh:mm
      const endTime = endDate.toISOString().slice(0, 16);
      setData('end_at', endTime);
    }
  };

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Button variant="outline" className="mb-4" asChild>
              <Link href={selectedFacility ? `/facility_owner/facilities/${selectedFacility.id}` : "/facility_owner/tour_slots"}>
                <FiArrowLeft className="mr-2 h-4 w-4" />
                {selectedFacility ? `Back to ${selectedFacility.name}` : 'Back to Tour Slots'}
              </Link>
            </Button>
            
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Add New Tour Slot</h1>
                <p className="text-gray-500">
                  {selectedFacility 
                    ? `Create a new tour slot for ${selectedFacility.name}` 
                    : 'Create a new available time slot for facility tours'}
                </p>
              </div>
            </div>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Tour Slot Details</CardTitle>
              <CardDescription>Specify when this tour slot will be available</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="facility_id">Facility</Label>
                    {selectedFacility ? (
                      <div className="mb-2">
                        <div className="flex items-center p-3 border rounded-md bg-blue-50 border-blue-200">
                          <FiHome className="mr-2 h-4 w-4 text-blue-500" />
                          <span className="font-medium">{selectedFacility.name}</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="ml-auto h-7 px-2 text-blue-600" 
                            onClick={() => setData('facility_id', '')}
                          >
                            Change
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Select
                        value={data.facility_id}
                        onValueChange={(value) => setData('facility_id', value)}
                      >
                        <SelectTrigger id="facility_id">
                          <SelectValue placeholder="Select a facility" />
                        </SelectTrigger>
                        <SelectContent>
                          {facilities.map((facility) => (
                            <SelectItem key={facility.id} value={facility.id.toString()}>
                              {facility.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {errors.facility_id && <p className="text-red-500 text-sm mt-1">{errors.facility_id}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="start_at">Start Time</Label>
                    <div className="flex items-center">
                      <FiCalendar className="mr-2 h-4 w-4 text-gray-400" />
                      <Input
                        id="start_at"
                        type="datetime-local"
                        value={data.start_at}
                        onChange={handleStartTimeChange}
                        className={errors.start_at ? 'border-red-500' : ''}
                      />
                    </div>
                    {errors.start_at && <p className="text-red-500 text-sm mt-1">{errors.start_at}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="end_at">End Time</Label>
                    <div className="flex items-center">
                      <FiCalendar className="mr-2 h-4 w-4 text-gray-400" />
                      <Input
                        id="end_at"
                        type="datetime-local"
                        value={data.end_at}
                        onChange={(e) => setData('end_at', e.target.value)}
                        className={errors.end_at ? 'border-red-500' : ''}
                      />
                    </div>
                    {errors.end_at && <p className="text-red-500 text-sm mt-1">{errors.end_at}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={data.status}
                      onValueChange={(value) => setData('status', value)}
                    >
                      <SelectTrigger className={errors.status ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.status && <p className="text-red-500 text-sm mt-1">{errors.status}</p>}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" asChild>
                    <Link href="/facility_owner/tour_slots">Cancel</Link>
                  </Button>
                  <Button type="submit" disabled={processing}>
                    Create Tour Slot
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
